import React from 'react';
import { Typography, Row, Col, Table, Tag, theme as antdTheme } from 'antd';
import { usePermissions } from '../../../hooks/usePermissions';
import { splitModeLabel } from '../../../components/PaymentSplitCells';

const { Text } = Typography;
const { useToken } = antdTheme;

const MODE_LABELS = {
  CASH: 'Cash',
  BANK_TRANSFER: 'Bank Transfer',
  UPI: 'UPI',
  CHEQUE: 'Cheque',
  RTGS: 'RTGS',
  NEFT: 'NEFT',
  CREDIT_CARD: 'Credit Card',
  AUTO_DEBIT: 'Auto Debit',
  INTERNAL_TRANSFER: 'Internal Transfer',
  SPLIT: 'Split',
};

const modeLabel = (m) => MODE_LABELS[m] || String(m || '-').replace(/_/g, ' ');

const formatCurrency = (val) =>
  new Intl.NumberFormat('en-IN', {
    style: 'currency', currency: 'INR', maximumFractionDigits: 2,
  }).format(val || 0);

const formatDate = (d) => (d ? new Date(d).toLocaleDateString('en-IN') : '-');

/**
 * Flatten the bill's payment trail to one row per money movement.
 * A payment split across modes contributes one row per split — that is where the
 * per-mode reference numbers live, and a single "Split" row would hide them.
 */
const toRows = (paymentDetails = []) => {
  const rows = [];
  paymentDetails.forEach((p, pi) => {
    if (Array.isArray(p.splits) && p.splits.length > 0) {
      p.splits.forEach((s, si) => {
        rows.push({
          key: `${pi}-${si}`,
          paymentId: p.paymentId,
          paymentDate: p.paymentDate,
          // The rail the approver recorded (IMPS / NEFT) when there is one — the
          // same label the payment voucher prints in its Mode column.
          mode: splitModeLabel(s),
          reference: s.reference || p.transactionReference || '',
          amount: s.amount || 0,
        });
      });
    } else {
      rows.push({
        key: `${pi}`,
        paymentId: p.paymentId,
        paymentDate: p.paymentDate,
        mode: p.mode,
        reference: p.transactionReference || '',
        amount: p.amount || 0,
      });
    }
  });
  return rows;
};

const PaymentAndRemarksView = ({ form }) => {
  const { token } = useToken();
  const { canViewField } = usePermissions();

  const canViewPaymentMethod = canViewField('billing', 'paymentMethod');

  // The customer is paid BEFORE this bill is raised, so when the trail is
  // present it replaces the bare payment-mode line.
  const rows = toRows(form.paymentDetails);
  const totalPaid = form.totalPaid
    ?? rows.reduce((sum, r) => sum + (r.amount || 0), 0);

  const columns = [
    { title: '#', width: 50, render: (_, __, i) => i + 1 },
    { title: 'Date', dataIndex: 'paymentDate', render: formatDate },
    { title: 'Payment ID', dataIndex: 'paymentId', render: (v) => v || '-' },
    { title: 'Mode', dataIndex: 'mode', render: (v) => <Tag>{modeLabel(v)}</Tag> },
    { title: 'Reference No.', dataIndex: 'reference', render: (v) => v || '-' },
    {
      title: 'Amount',
      dataIndex: 'amount',
      align: 'right',
      render: (v) => <strong>{formatCurrency(v)}</strong>,
    },
  ];

  return (
    <Row gutter={[16, 16]} style={{ marginTop: token.marginSM }}>
      {/* Payment details */}
      {canViewPaymentMethod && (
        <Col xs={24}>
          <section className="bill-panel">
            <h4 className="bill-panel__title">Payment Details</h4>
            {rows.length > 0 ? (
              <Table
                dataSource={rows}
                columns={columns}
                pagination={false}
                size="small"
                bordered
                scroll={{ x: 'max-content' }}
                summary={() => (
                  <Table.Summary.Row className="bill-items__final">
                    <Table.Summary.Cell index={0} colSpan={5} align="right">
                      <Text strong>Total Paid to Seller</Text>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={1} align="right">
                      <Text strong style={{ color: '#389e0d', fontSize: 15 }}>
                        {formatCurrency(totalPaid)}
                      </Text>
                    </Table.Summary.Cell>
                  </Table.Summary.Row>
                )}
              />
            ) : (
              <Text>Paid by <Tag>{modeLabel(form.paymentMode)}</Tag></Text>
            )}
          </section>
        </Col>
      )}

      {/* Remarks */}
      {form.remarks ? (
        <Col xs={24}>
          <section className="bill-panel">
            <h4 className="bill-panel__title">Remarks</h4>
            <Text
              style={{
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                display: 'block',
              }}
            >
              {form.remarks}
            </Text>
          </section>
        </Col>
      ) : null}
    </Row>
  );
};

export default PaymentAndRemarksView;
